import { useState, useEffect, useMemo, useTransition } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type CalculatorToolClient, iconMap } from '@/lib/calculator-data';
import { calculatorDefinitions, type FieldDefinition, type CalculatorDefinition } from '@/lib/calculator-definitions';
import * as format from '@/lib/utils';
import { motion } from 'framer-motion';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { AlertCircle, Info, Calculator, Sparkles, CheckCircle, HelpCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '../ui/skeleton';

const formatResult = (formatType: string, value: number | null) => {
  switch (formatType) {
    case 'currency':
      return format.formatCurrency(value);
    case 'number':
      return format.formatNumber(value);
    case 'percentage':
      return format.formatPercentage(value);
    case 'months':
      return format.formatMonths(value);
    case 'years':
      return format.formatYears(value);
    default:
      return String(value);
  }
};

const renderField = (fieldDef: FieldDefinition, control: any, form: any) => {
  const commonProps = {
    control: control,
    name: fieldDef.name,
  };

  const label = (
    <div className="flex items-center gap-1.5">
      <FormLabel>{fieldDef.label}</FormLabel>
      {fieldDef.description && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Field description">
                <Info className="w-4 h-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{fieldDef.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  switch (fieldDef.type) {
    case 'slider':
      return (
        <FormField
          {...commonProps}
          key={fieldDef.name}
          render={({ field }) => (
            <FormItem>
              {label}
              <div className="flex items-center gap-4">
                <FormControl>
                  <Slider
                    min={fieldDef.min}
                    max={fieldDef.max}
                    step={fieldDef.step}
                    value={[field.value]}
                    onValueChange={(value) => field.onChange(value[0])}
                  />
                </FormControl>
                <span className="text-sm font-medium w-20 text-right">{field.value}{fieldDef.label.includes('%') ? '%' : ''}</span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    case 'select':
      return (
        <FormField
          {...commonProps}
          key={fieldDef.name}
          render={({ field }) => (
            <FormItem>
              {label}
               <Select onValueChange={field.onChange} defaultValue={String(field.value)}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${fieldDef.label}`} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {fieldDef.options?.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    case 'number':
    default:
      return (
        <FormField
          {...commonProps}
          key={fieldDef.name}
          render={({ field }) => (
            <FormItem>
              {label}
              <FormControl>
                <Input type="number" placeholder={String(fieldDef.defaultValue)} {...field} />
              </FormControl>              <FormMessage />
            </FormItem>
          )}
        />
      );
  }
};

function ComingSoonCard({ toolName }: { toolName: string }) {
  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="font-headline text-3xl">{toolName}</CardTitle>
      </CardHeader>
      <CardContent>
        <Alert>
          <Calculator className="h-4 w-4" />
          <AlertTitle>Coming Soon!</AlertTitle>
          <AlertDescription>
            This calculator is currently under development. Check back later!
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}


export function CalculatorClientPage({ calculator, content }: { calculator: CalculatorToolClient, content: React.ReactNode }) {
  const [results, setResults] = useState<Record<string, number | null> | null>(null);
  const [isPending, startTransition] = useTransition();

  const definition = useMemo(() => (calculatorDefinitions as any)[calculator.slug] as CalculatorDefinition | undefined, [calculator.slug]);

  const form = useForm({
    resolver: definition ? zodResolver(definition.schema) : undefined,
    defaultValues: definition ? definition.fields.reduce((acc, field) => ({ ...acc, [field.name]: field.defaultValue }), {}) : {},
    mode: 'onChange',
  });

  const { watch, control, formState, trigger } = form;

  useEffect(() => {
    // Trigger validation on mount to show any initial errors if needed
    trigger();
  }, [trigger]);


  useEffect(() => {
    const subscription = watch((values) => {
      if (formState.isValid && definition) {
        startTransition(() => {
          const newResults = definition.calculation(values);
          setResults(newResults);
        });
      } else {
        setResults(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, formState.isValid, definition]);

  useEffect(() => {
    // Initial calculation on load
    if (formState.isValid && definition) {
      const initialValues = form.getValues();
      const newResults = definition.calculation(initialValues);
      setResults(newResults);
    }
  }, [form, formState.isValid, definition]);


  if (!definition) {
    return <ComingSoonCard toolName={calculator.name} />;
  }
  
  const isSpecialCalculator = ['video-creator-revenue', 'tiktok-creator-fund', 'onlyfans-tax-calculator', 'instagram-influencer-rate', 'patreon-earnings-estimator', 'ko-fi-creator-subscription', 'facebook-creator-earnings', 'snapchat-story-revenue', 'pinterest-monetization', 'twitter-x-earnings-estimator', 'freelance-income-planner', 'gig-worker-earnings-calculator', 'gig-worker-tax-estimator', 'remote-work-financial-calculator', 'retainer-contract-profit', 'project-roi', 'time-vs-money-tracker', 'tax-invoice-hub', 'fiverr-project-profit', 'upwork-contract-profit', 'freelancer-com-rate-roi', 'gig-worker-fuel-maintenance-estimator', 'multi-platform-gig-income-tracker', 'elite-freelancer-roi-planner', 'uber-lyft-estimator', 'reddit-community-monetization', 'twitch-streamer-revenue', 'kick-streaming-income', 'shopify-store-roi', 'discord-monetization-planner', 'kickstarter-crowdfunding-roi', 'digital-product-profit'].includes(calculator.slug);

  const Icon = iconMap[calculator.iconName as keyof typeof iconMap] || HelpCircle;

  const heroSection = isSpecialCalculator ? (
    <header className="text-center mb-10 py-16 rounded-2xl bg-gradient-to-br from-purple-600 via-blue-500 to-pink-500 text-white shadow-2xl shadow-primary/30">
        <h1 className="font-headline text-4xl md:text-5xl font-bold tracking-tighter mb-2">{calculator.name}</h1>
        <p className="text-lg text-white/80 max-w-2xl mx-auto">{calculator.description}</p>
    </header>
  ) : (
     <header className="text-center mb-10">
        <div className="inline-block bg-primary/10 p-3 rounded-2xl mb-4 shadow-inner">
          <Icon className="w-10 h-10 text-primary" />
        </div>
        <h1 className="font-headline text-4xl md:text-5xl font-bold tracking-tighter mb-2">{calculator.name}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{calculator.description}</p>
      </header>
  );


  return (
    <>
    <div className="container mx-auto px-4 py-8">
      {heroSection}

      <Card className="overflow-hidden shadow-2xl shadow-primary/10">
        <div className="grid md:grid-cols-2">
          <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r">
            <CardHeader className="p-0 mb-6">
              <CardTitle className="font-headline text-2xl">Inputs</CardTitle>
              <CardDescription>Adjust the values to see your results update instantly.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Form {...form}>
                <form className="space-y-6">
                  {definition.fields.map((fieldDef) => renderField(fieldDef, control, form))}
                </form>
              </Form>
            </CardContent>
          </div>

          <div className="p-6 md:p-8 bg-gradient-to-br from-primary/5 via-background to-background flex flex-col justify-center">
            <CardHeader className="p-0 mb-6">
              <CardTitle className="font-headline text-2xl">Results</CardTitle>
              <CardDescription>Your calculated financial metrics.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-4">
                {definition.results.map((resultDef) => (
                  <motion.div 
                    key={resultDef.key} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="p-4 rounded-lg bg-background/80 border"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="text-sm font-medium text-muted-foreground">{resultDef.label}</h3>
                      {resultDef.description && (
                         <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" aria-label="Result description">
                                  <Info className="w-4 h-4 text-muted-foreground" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{resultDef.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                      )}
                    </div>
                    {isPending ? (
                      <Skeleton className="h-10 w-3/4" />
                    ) : (
                      <p className="text-3xl lg:text-4xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-400 flex items-baseline gap-2">
                         <span>{formatResult(resultDef.format, results ? results[resultDef.key] : null)}</span>
                         {resultDef.format === 'months' && <span className="text-lg font-medium text-muted-foreground">{formatResult('years', results ? results['yearsToGoal'] : null)}</span>}
                      </p>
                    )}
                  </motion.div>
                ))}
                 {!formState.isValid && (
                    <Alert variant="destructive" className="animate-in fade-in duration-300">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Invalid Input</AlertTitle>
                        <AlertDescription>
                        Please check your inputs. All fields must be positive numbers.
                        </AlertDescription>
                    </Alert>
                )}
                 {isSpecialCalculator && results && formState.isValid && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Card className="mt-4 bg-blue-50 border-blue-200">
                            <CardHeader>
                                <CardTitle className="text-lg text-blue-900 flex items-center gap-2"><Sparkles className="w-5 h-5"/>Results Explained</CardTitle>
                            </CardHeader>
                            <CardContent className="text-blue-800 space-y-2 text-sm">
                                {calculator.slug === 'video-creator-revenue' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Monthly/annual income is net of platform fees and taxes.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>RPM and engagement rate are key drivers of ad revenue.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Sponsorships and memberships can significantly boost earnings.</span></p>
                                    </>
                                ) : calculator.slug === 'tiktok-creator-fund' ? (
                                     <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Platform-Specific RPM: Calculates earnings per 1,000 views.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Engagement Matters: Adjusts payout based on engagement quality.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Country Effect: Shows difference in RPM globally.</span></p>
                                    </>
                                ) : calculator.slug === 'onlyfans-tax-calculator' ? (
                                    <>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Platform Fee Transparency: Shows 20% OnlyFans cut.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Real-World Taxes: Adjusts for country-specific tax rates.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Business Expense Impact: Highlights how deductions save money.</span></p>
                                   </>
                                ) : calculator.slug === 'instagram-influencer-rate' ? (
                                    <>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Follower count and engagement drive rates.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Niche multipliers reflect industry averages for pricing.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Video posts and story content increase your value.</span></p>
                                   </>
                                ) : calculator.slug === 'patreon-earnings-estimator' ? (
                                    <>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Monthly and annual earnings are shown after all fees and taxes.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Platform and processing fees are key deductions from gross income.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Business expenses lower your taxable income, saving you money.</span></p>
                                   </>
                                ) : calculator.slug === 'ko-fi-creator-subscription' ? (
                                    <>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Net income is shown after platform fees, payment processing, and taxes.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Ko-fi's 0% platform fee (for Gold members) can significantly increase earnings.</span></p>
                                       <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Business expenses are deducted before taxes, lowering your tax bill.</span></p>
                                   </>
                                ) : calculator.slug === 'facebook-creator-earnings' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Net earnings are calculated after all fees, expenses, and taxes.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Ad Break CPM and fan subscriptions are key income drivers.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Branded content deals can significantly boost your total revenue.</span></p>
                                    </>
                                ) : calculator.slug === 'snapchat-story-revenue' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows estimated Snapchat earnings per month after all deductions.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of views, sponsorships, and platform fees.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Helps you plan content and monetization strategies for Snapchat.</span></p>
                                    </>
                                ) : calculator.slug === 'pinterest-monetization' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows estimated monthly Pinterest earnings from affiliate links and sponsorships.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of CTR, conversion rates, and expenses on your net income.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Helps you optimize your pin strategy for maximum profitability.</span></p>
                                    </>
                                ) : calculator.slug === 'twitter-x-earnings-estimator' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows estimated earnings from Twitter/X monetization features.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of tips, Super Follows, and sponsored tweets.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Helps plan content strategy and revenue streams.</span></p>
                                    </>
                                ) : calculator.slug === 'gig-worker-earnings-calculator' ? (
                                  <>
                                     <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows impact of platform fees and tax deductions.</span></p>
                                     <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights expenses like fuel and maintenance.</span></p>
                                     <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Projects monthly and annual earnings.</span></p>
                                 </>
                                ) : calculator.slug === 'gig-worker-tax-estimator' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Displays tax obligations clearly.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows effect of business and vehicle deductions.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights monthly vs annual comparison.</span></p>
                                    </>
                                ) : calculator.slug === 'remote-work-financial-calculator' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Calculates take-home income after taxes and retirement.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights savings allocation and monthly expenses.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows annual projections for financial planning.</span></p>
                                    </>
                                ) : calculator.slug === 'retainer-contract-profit' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows profitability per retainer and contract.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights effect of variable expenses and labor cost.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Monthly and annual breakdowns for planning.</span></p>
                                    </>
                                ) : calculator.slug === 'project-roi' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows profitability and ROI per project.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights cost allocation: labor, fixed, variable.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Risk-adjusted ROI for realistic planning.</span></p>
                                    </>
                                ) : calculator.slug === 'time-vs-money-tracker' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Calculates your 'Effective Hourly Rate' for a true measure of profitability.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the most profitable income streams (hourly vs. project vs. retainer).</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows how non-billable hours impact your overall earnings.</span></p>
                                    </>
                                ) : calculator.slug === 'tax-invoice-hub' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows clear tax obligations based on your income and deductions.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of business and expenses on your net income.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Provides a clear breakdown for monthly and annual financial planning.</span></p>
                                    </>
                                ) : calculator.slug === 'fiverr-project-profit' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows profitability per project and month after all fees and taxes.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of Fiverr's 20% service fee on your gross income.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Allows for planning multiple projects to forecast monthly income.</span></p>
                                    </>
                                ) : calculator.slug === 'upwork-contract-profit' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows profitability per contract after Upwork's fees and taxes.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Models both hourly and fixed-price contract types for accurate planning.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of business expenses on your net profit.</span></p>
                                    </>
                                ) : calculator.slug === 'freelancer-com-rate-roi' ? (
                                    <>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows profitability and ROI per project.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the effect of Freelancer.com fees, expenses, and taxes.</span></p>
                                        <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Allows for planning multiple projects and rates.</span></p>
                                    </>
                                ) : calculator.slug === 'gig-worker-fuel-maintenance-estimator' ? (
                                    <>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Your weekly, monthly, and annual net earnings after all costs.</span></p>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows the impact of platform fees, fuel, and maintenance on your bottom line.</span></p>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Helps you understand the true cost of your gig work vehicle.</span></p>
                                    </>
                                ) : calculator.slug === 'multi-platform-gig-income-tracker' ? (
                                    <>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Shows your combined net income across all entered platforms.</span></p>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Highlights the impact of separate fees and expenses for each platform.</span></p>
                                      <p className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0"/><span>Helps you identify which platforms are most profitable for you.</span></p>
                                    </>
                                ) : null}
                                <p className="mt-4 text-xs italic">Disclaimer: Estimates only, actual results vary based on platform changes.</p>
                            </CardContent>
                        </Card>
                    </motion.div>
                 )}
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
      {content}
      </div>
    </>
  );
}
